// Share a recipe (redesign slice 8a; wireframe 11;
// docs/2026-10-04-sharing-design.md, section 1). One sheet, for the
// recipe's owner, replacing the old sharing panel:
//
//   Send or post it    a preview of what people see, "Share to an app" (the
//                      phone's own share menu), "Copy link", and the
//                      recipe's one link, on or off. Using either button
//                      while the link is off turns it on first.
//   Share with someone by email, with "Let them edit it" (set before the
//                      first share; locked while shared), and Remove.
//   Who else can see it your household (set in household settings), and
//                      everyone on The Weekly Kitchen (public), which can
//                      be turned off again here.
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useMealPlanner } from "../hooks/MealPlannerContext";
import { createRecipeShareLink, listRecipeShareLinks, listRecipeShares, loadShareRecipesWithHousehold, revokeRecipeShare, revokeRecipeShareLink, shareRecipe } from "../lib/db";
import { COLORS, FONT_SIZE, LAYOUT, RADIUS, SPACE } from "../lib/constants";
import { libraryMeta } from "../lib/recipeLibrary";
import type { Recipe } from "../lib/types";
import { Sheet } from "./Sheet";
import { Toggle } from "./Toggle";
import { smallLabel, supporting } from "./recipeStyles";
import { inputStyle, textButton } from "./plan/planStyles";
import { primaryButton, secondaryButton } from "./recipes/writeStyles";

interface ShareRow {
  id: string;
  email: string;
  shared_with_user_id: string;
  origin?: string;
}
interface LinkRow {
  id: string;
  token: string;
}

const recipeLinkUrl = (token: string) => `${window.location.origin}/shared/${token}`;

export function RecipeShareSheet({ recipe, onClose }: { recipe: Recipe; onClose: () => void }) {
  const mp = useMealPlanner();
  const [shares, setShares] = useState<ShareRow[]>([]);
  const [link, setLink] = useState<LinkRow | null>(null);
  const [household, setHousehold] = useState<boolean | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [email, setEmail] = useState("");
  const [canEdit, setCanEdit] = useState(!!recipe.editableByOthers);
  const [busy, setBusy] = useState<"" | "share" | "link" | "public">("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([listRecipeShares(recipe.id), listRecipeShareLinks(recipe.id), loadShareRecipesWithHousehold(mp.userId, mp.householdId).catch(() => null)])
      .then(([shareRows, linkRows, hh]) => {
        if (cancelled) return;
        setShares(shareRows as ShareRow[]);
        setLink(((linkRows as LinkRow[]) || [])[0] ?? null);
        setHousehold(hh as boolean | null);
      })
      .catch(() => !cancelled && setError("Couldn't load how this recipe is shared."))
      .finally(() => !cancelled && setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, [recipe.id, mp.userId, mp.householdId]);

  // Shares made by two households importing the same link aren't anyone's
  // choice and are never shown (0033); they still count for the edit lock.
  const isFirstShare = shares.length === 0;
  const people = shares.filter((s) => s.origin !== "url_import");

  async function linkOn(): Promise<LinkRow | null> {
    if (link) return link;
    setBusy("link");
    setError("");
    try {
      const made = (await createRecipeShareLink(recipe.id)) as LinkRow;
      setLink(made);
      return made;
    } catch (e) {
      setError((e as Error).message || "Couldn't turn the link on.");
      return null;
    } finally {
      setBusy("");
    }
  }

  async function linkOff() {
    if (!link) return;
    setBusy("link");
    setError("");
    try {
      await revokeRecipeShareLink(link.id);
      setLink(null);
    } catch (e) {
      setError((e as Error).message || "Couldn't turn the link off.");
    } finally {
      setBusy("");
    }
  }

  async function shareToApp() {
    const l = await linkOn();
    if (!l) return;
    try {
      await navigator.share({ title: recipe.title, text: [recipe.title, libraryMeta(recipe)].filter(Boolean).join(" · "), url: recipeLinkUrl(l.token) });
    } catch {
      // Closing the share menu without choosing is not an error.
    }
  }

  async function copyLink() {
    const l = await linkOn();
    if (!l) return;
    try {
      await navigator.clipboard.writeText(recipeLinkUrl(l.token));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Couldn't copy. The link is shown below to copy by hand.");
    }
  }

  async function share(e: FormEvent) {
    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy("share");
    setError("");
    try {
      await shareRecipe(recipe.id, email.trim(), canEdit, isFirstShare);
      if (isFirstShare) mp.noteEditableByOthers(recipe.id, canEdit);
      setShares((await listRecipeShares(recipe.id)) as ShareRow[]);
      setEmail("");
    } catch (err) {
      setError((err as Error).message || "Couldn't share this recipe.");
    } finally {
      setBusy("");
    }
  }

  async function remove(userId: string) {
    setBusy("share");
    setError("");
    try {
      await revokeRecipeShare(recipe.id, userId);
      setShares((await listRecipeShares(recipe.id)) as ShareRow[]);
    } catch (err) {
      setError((err as Error).message || "Couldn't remove that share.");
    } finally {
      setBusy("");
    }
  }

  async function setPublic(on: boolean) {
    setBusy("public");
    setError("");
    try {
      await mp.setRecipePublic(recipe.id, on);
    } catch (err) {
      setError((err as Error).message || "Couldn't change that.");
    } finally {
      setBusy("");
    }
  }

  const canShareToApp = typeof navigator !== "undefined" && typeof navigator.share === "function";

  return (
    <Sheet title="Share this recipe" onClose={onClose}>
      {!loaded ? (
        <p style={supporting}>Loading…</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: SPACE.x3 }}>
          {error && <p style={{ color: COLORS.danger, margin: 0 }}>{error}</p>}

          <Part title="Send or post it">
            <div style={{ border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.xl, padding: "var(--space-lg) var(--space-xl)", background: COLORS.card }}>
              <div style={{ fontFamily: "var(--font-serif)", fontSize: FONT_SIZE.h4, fontWeight: 600 }}>{recipe.title}</div>
              <div style={supporting}>{[libraryMeta(recipe), window.location.host].filter(Boolean).join(" · ")}</div>
            </div>
            <p style={{ ...supporting, margin: 0 }}>This is the preview people see in a message or post.</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: SPACE.md }}>
              {canShareToApp && (
                <button type="button" onClick={shareToApp} disabled={!!busy} style={primaryButton}>
                  Share to an app
                </button>
              )}
              <button type="button" onClick={copyLink} disabled={!!busy} style={canShareToApp ? secondaryButton : primaryButton}>
                {copied ? "Copied" : "Copy link"}
              </button>
            </div>
            <Toggle on={!!link} disabled={busy === "link"} label={link ? "Link is on" : "Link is off"} onChange={(on) => (on ? linkOn() : linkOff())}>
              {link ? "Anyone with the link can view the full recipe. No account needed. Turn it off to stop every copy of the link." : "Turn it on to send or post the recipe. Anyone with the link can view it; no account needed."}
            </Toggle>
            {link && <input readOnly aria-label="The recipe's link" value={recipeLinkUrl(link.token)} onFocus={(e) => e.target.select()} style={{ ...inputStyle, width: "100%", fontSize: FONT_SIZE.sm }} />}
          </Part>

          <Part title="Share with someone on The Weekly Kitchen">
            <p style={{ ...supporting, margin: 0 }}>It appears in their library and stays up to date with yours.</p>
            <form onSubmit={share} style={{ display: "flex", gap: SPACE.md, flexWrap: "wrap" }}>
              <input type="email" aria-label="Their email" placeholder="Their email" value={email} onChange={(e) => setEmail(e.target.value)} style={{ ...inputStyle, flex: "1 1 200px", minWidth: 0 }} />
              <button type="submit" disabled={!email.trim() || !!busy} style={{ ...primaryButton, opacity: email.trim() ? 1 : 0.6 }}>
                Share
              </button>
            </form>
            {isFirstShare ? (
              <label style={{ display: "flex", alignItems: "center", gap: SPACE.md, fontSize: FONT_SIZE.base }}>
                <input type="checkbox" className="mp-check" checked={canEdit} onChange={(e) => setCanEdit(e.target.checked)} />
                Let them edit it
              </label>
            ) : (
              <span style={supporting}>They {canEdit ? "can" : "can't"} edit it. This is fixed while the recipe is shared.</span>
            )}
            <span style={supporting}>Set this before the first share. It locks while the recipe is shared.</span>
            {people.map((s) => (
              <div key={s.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE.md, borderBottom: `1px solid ${COLORS.line}`, minHeight: LAYOUT.tapTarget }}>
                <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                  {s.email}
                  <span style={supporting}> · {canEdit ? "can edit" : "can view"}</span>
                </span>
                <button type="button" onClick={() => remove(s.shared_with_user_id)} disabled={!!busy} style={{ ...textButton, color: COLORS.danger, fontSize: FONT_SIZE.sm }}>
                  Remove
                </button>
              </div>
            ))}
          </Part>

          <Part title="Who else can see it">
            <div style={{ display: "flex", justifyContent: "space-between", gap: SPACE.md, alignItems: "flex-start" }}>
              <span>
                <strong>Your household</strong>
                <span style={{ ...supporting, display: "block" }}>
                  {household === null ? "Set for all your recipes. " : `${household ? "On" : "Off"} for all your recipes. `}
                  <Link to="/defaults" style={{ color: COLORS.accent }}>
                    Change it in household settings
                  </Link>
                </span>
              </span>
              {household !== null && <strong style={{ color: household ? COLORS.accent : COLORS.soft, flexShrink: 0 }}>{household ? "On" : "Off"}</strong>}
            </div>
            <Toggle on={!!recipe.isPublic} disabled={busy === "public"} label="Everyone on The Weekly Kitchen" onChange={setPublic}>
              Lists it publicly. A public recipe can&apos;t be edited until you turn this off.
            </Toggle>
          </Part>
        </div>
      )}
    </Sheet>
  );
}

function Part({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: SPACE.md }}>
      <h3 style={{ ...smallLabel, color: COLORS.soft, margin: 0 }}>{title}</h3>
      {children}
    </section>
  );
}
